<template>
  <div class="kpt-support-ticket-create">
    <div class="kpt-support-ticket-content">
      <div class="field">
        <label class="label">Contact</label>
        <detail-field label-width="90">
          <template v-slot:label>Name</template>
          <template v-slot:value>{{ user.firstName }} {{ user.lastName }}</template>
        </detail-field>
        <detail-field label-width="90">
          <template v-slot:label>Email</template>
          <template v-slot:value>{{ user.email }}</template>
        </detail-field>
      </div>
      <b-field grouped>
        <b-field label="Type" class="kpt-ticket-type-select" required>
          <b-select placeholder="Select a type" v-model="ticket.type" @input="typeSelected" expanded required>
            <option
              v-for="type in ticketTypes"
              :value="type.displayName"
              :key="type.order">
              {{ type.displayName }}
            </option>
          </b-select>
        </b-field>
        <b-field label="Sub Type" expanded required>
          <b-select placeholder="Select a sub type" v-model="ticket.subType" :disabled="!ticket.type" expanded required>
            <option
              v-for="type in ticketSubTypes"
              :value="type.displayName"
              :key="type.order">
              {{ type.displayName }}
            </option>
          </b-select>
        </b-field>
      </b-field>
      <b-field label="Other Explanation" v-if="ticket.subType == 'Other'" :required="ticket.subType == 'Other'">
        <b-input v-model="ticket.otherExplanation" placeholder="Enter an explanation for 'Other'" maxlength="1000" type="textarea" :required="ticket.subType == 'Other'"></b-input>
      </b-field>
      <b-field label="Priority" required>
        <b-radio-button v-model="ticket.priority"
          v-for="priority in ticketPriorities"
          :key="priority.value"
          :native-value="priority.displayName"
          :type="`${priority.type} is-light is-outlined`" required>
          <span>{{ priority.displayName }}</span>
        </b-radio-button>
      </b-field>
      <b-field label="Subject" expanded required>
        <b-input v-model="ticket.subject" placeholder="Enter a subject" expanded required></b-input>
      </b-field>
      <b-field label="Description" required>
        <b-input v-model="ticket.description" placeholder="Enter a description" maxlength="1000" type="textarea" required></b-input>
      </b-field>
      <b-field label="Impact" expanded :required="ticket.type == 'Problem'">
        <b-select placeholder="Select an impact" v-model="ticket.impact" expanded :required="ticket.type == 'Problem'">
          <option
            v-for="impact in ticketImpacts"
            :value="impact.displayName"
            :key="impact.value">
            {{ impact.displayName }}
          </option>
        </b-select>
      </b-field>
      <div class="field">
        <label class="label">Remotes</label>
        <b-message type="is-info" v-if="ticket.impact == 'Multiple Terminals'">
          Please select the most relevant Terminal (select one).
        </b-message>
        <terminal-selector @item-select="remoteSelected"></terminal-selector>
      </div>
      <b-field grouped>
        <b-field label="Modem Serial" expanded>
          <b-input v-model="ticket.modemSerial" placeholder="Enter serial number" expanded></b-input>
        </b-field>
        <b-field label="Antenna Serial" expanded>
          <b-input v-model="ticket.antennaSerial" placeholder="Enter serial number" expanded></b-input>
        </b-field>
      </b-field>
    </div>
    <footer class="kpt-ticket-actions">
      <div class="buttons">
        <b-tooltip label="Please complete all required fields." :active="!canSubmit" type="is-warning" class="pr-2">
          <b-button size="is-small" type="is-primary" @click="createTicket" :disabled="!canSubmit">Submit</b-button>
        </b-tooltip>
        <b-button size="is-small" type="is-outlined" @click="$emit('cancel')">Cancel</b-button>
      </div>
    </footer>
  </div>
</template>

<script>
import TerminalSelectorComponent from '../../terminals/components/TerminalSelectorComponent.vue';

export default {
  components: {
    'terminal-selector': TerminalSelectorComponent
  },
  data() {
    return {
      ticket: {
        type: null,
        subType: null,
        priority: 'Medium',
        otherExplanation: null,
        impact: null,
        subject: null,
        description: null,
        antennaSerial: null,
        modemSerial: null
      }
    }
  },
  methods: {
    typeSelected() {
      this.ticket.subType = null;
    },
    remoteSelected(selectedRemotes) {
      console.log(selectedRemotes);
      const remote = selectedRemotes && selectedRemotes.length ? selectedRemotes[0] : null;
      if (!remote) {
        this.ticket.antennaSerial = null;
        this.ticket.modemSerial = null;
        return;
      }
      this.ticket.antennaSerial = remote?.hardware?.antenna?.serial;
      this.ticket.modemSerial = remote?.hardware?.geoModem?.serial;
    },
    // eslint-disable-next-line consistent-return
    async createTicket() {
      // dispatch call to salesforce
      const isSuccess = await this.$store.dispatch('createSalesforceCase', this.ticket);
      if (!isSuccess) {
        // indicate failure to user
        this.$buefy.toast.open({
          duration: 2000,
          message: 'Error encountered while attempting to create ticket.',
          type: 'is-danger'
        });
        return null;
      }
      // indicate success to user
      this.$buefy.toast.open({
        duration: 3000,
        message: 'Your ticket has been submitted!',
        type: 'is-success'
      });      
      // KyAnalytics client plugin, event tracker
      this.$kya.event('support', 'createSupportTicket', 'Support ticket has been created', { payload: this.ticket });
      // cancel out of the form now that it has been submitted successfully
      this.$emit('cancel');
    }
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    canSubmit() {
      // impact is required if type is "Problem"
      if (this.ticket.type == 'Problem' && !this.ticket.impact) return false;
      // explanation is required when sub-type is "Other"
      if (this.ticket.subType == 'Other' && !this.ticket.otherExplanation) return false;
      // verify all other required fields
      return (this.ticket.type)
        && (this.ticket.subType)
        && (this.ticket.priority)
        && (this.ticket.subject)
        && (this.ticket.description);
    },
    sfContextValues() {
      return this.$store.state.appConfig.support.ticket.sfContextValues;
    },
    fieldMappings() {
      // these are custom fields in Salesforce which have custom field names (required for posting)
      return this.$store.state.appConfig.support.ticket.fieldMappings;
    },
    ticketTypes() {
      return this.$store.state.appConfig.support.ticket.types;
    },
    ticketSubTypes() {
      if (!this.ticket.type) return [];
      const selectedType = this.ticketTypes.find(tt => tt.displayName == this.ticket.type);
      if (!selectedType) return [];
      return selectedType.subTypes;
    },
    ticketPriorities() {
      return this.$store.state.appConfig.support.ticket.priorities;
    },
    priorityClass() {
      switch (this.ticket.priority) {
        case 0:
          return 'is-info';
        case 1:
          return 'is-warning';
        case 2:
          return 'is-danger';
        default:
          return 'is-danger';
      }
    },
    ticketImpacts() {
      return this.$store.state.appConfig.support.ticket.impacts;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../styles/_colors';

.kpt-support-ticket-create {
  .kpt-ticket-type-select {
    width: 220px;
  }
  .kpt-support-ticket-content {
    .field.has-addons .control:not(:last-child) {
      margin-right: 0px;
    }
  }
  .kpt-ticket-actions {
    position: absolute;
    background: white;
    width: 100%;
    height: 52px;
    left: 0;
    bottom: 0;
    padding: 12px;
    border-top: 1px solid $grey-lighter;
  }
}
</style>
